<template>
  <div
    class="cma-asset-3d-loading"
    :class="{ 'cma-asset-3d-loading--reduced-motion': reduceMotion }"
  >
    <div class="cma-asset-3d-loading__bar-track">
      <div
        v-if="isDeterminate"
        class="cma-asset-3d-loading__bar-fill"
        :style="{ width: `${progress}%` }"
      />
      <div
        v-else
        class="cma-asset-3d-loading__bar-fill cma-asset-3d-loading__bar-fill--indeterminate"
      />
    </div>
    <p
      class="cma-asset-3d-loading__legend"
      role="status"
      aria-live="polite"
    >{{ currentInsightText }}</p>
  </div>
</template>

<script>
// Tempo entre trocas de linha da legenda quando o progresso é indeterminado
// (sem `total` do download, ou já em 100% aguardando o DRACO decodificar no
// worker — ver `isDeterminate` e `transformadorCad.js`): aí a legenda gira
// por tempo, não por `%`, ver docs/specs/cma-loading-3d-diagnostico.md §3.4.
const INSIGHT_CYCLE_INTERVAL_MS = 2600;

export default {
  name: 'CmaAsset3dLoading',
  props: {
    /** 0–100, ou `null` para modo indeterminado (sem `total` conhecido, ou
     *  aguardando decodificação Draco após o download terminar). */
    progress: {
      type: Number,
      default: null,
    },
    assetLabel: {
      type: String,
      default: '',
    },
    /** Linhas da legenda, já resolvidas pelo componente pai a partir de
     *  dados que o dashboard já carregou — ver contrato em
     *  docs/specs/cma-loading-3d-diagnostico.md §4. */
    insights: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      reduceMotion: false,
      cycleIndex: 0,
      cycleTimer: null,
    };
  },
  computed: {
    isDeterminate() {
      return typeof this.progress === 'number';
    },
    currentInsightText() {
      if (this.isDeterminate) {
        const active = this.insights.filter(item => item.at <= this.progress).pop();
        return ((active || this.insights[0]) || {}).text || '';
      }
      return (this.insights[this.cycleIndex] || this.insights[0] || {}).text || '';
    },
  },
  watch: {
    isDeterminate(determinate) {
      if (determinate) this.stopCycle();
      else this.maybeStartCycle();
    },
    insights() {
      this.cycleIndex = 0;
    },
  },
  mounted() {
    this.watchReducedMotion();
    this.maybeStartCycle();
  },
  beforeDestroy() {
    this.stopCycle();
    if (this.reducedMotionQuery) {
      this.reducedMotionQuery.removeEventListener('change', this.onReducedMotionChange);
    }
  },
  methods: {
    watchReducedMotion() {
      if (!window.matchMedia) return;
      const query = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reduceMotion = query.matches;
      if (!query.addEventListener) return;
      this.reducedMotionQuery = query;
      this.onReducedMotionChange = (event) => { this.reduceMotion = event.matches; };
      query.addEventListener('change', this.onReducedMotionChange);
    },
    maybeStartCycle() {
      if (this.isDeterminate || this.cycleTimer || this.insights.length < 2) return;
      this.cycleTimer = setInterval(() => {
        this.cycleIndex = (this.cycleIndex + 1) % this.insights.length;
      }, INSIGHT_CYCLE_INTERVAL_MS);
    },
    stopCycle() {
      if (!this.cycleTimer) return;
      clearInterval(this.cycleTimer);
      this.cycleTimer = null;
    },
  },
};
</script>

<style lang="sass" scoped>
@keyframes cma-asset-3d-loading-shimmer
  0%
    background-position: -150% 0
  100%
    background-position: 150% 0

.cma-asset-3d-loading
  position: absolute
  top: 0
  left: 0
  right: 0
  bottom: 0
  z-index: 5
  display: flex
  flex-direction: column
  align-items: center
  justify-content: center
  gap: 14px
  padding: 24px
  pointer-events: none
  background: rgba(10, 12, 14, 0.55)

  &__bar-track
    width: 70%
    max-width: 320px
    height: 6px
    border-radius: 6px
    overflow: hidden
    background: var(--color-background-regular)

  &__bar-fill
    height: 100%
    border-radius: 4px
    background: var(--color-primary)
    transition: width 0.2s ease-out

    &--indeterminate
      $base: var(--color-background-regular)
      width: 40%
      background: linear-gradient(90deg, $base 25%, var(--color-primary) 50%, $base 75%)
      background-size: 200% 100%
      animation: cma-asset-3d-loading-shimmer 1.6s ease-in-out infinite

  &__legend
    margin: 0
    font-size: 0.95rem
    font-weight: 500
    color: var(--color-white-100)
    text-align: center

  &--reduced-motion &__bar-fill--indeterminate
    animation: none
    background: var(--color-primary)
    opacity: 0.5
</style>
